import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { BaseButton } from '../../../shared/ui';
import { TipsStatementFilter } from '../model';

type TipsStatementFilterTabsProps = {
  activeFilter: TipsStatementFilter;
  onChange: (filter: TipsStatementFilter) => void;
};

const Tabs = styled.div.attrs({
  'data-ui': 'tips-statement-filter-tabs',
})`
  width: ${({ theme }) => theme.scale.px(263)};
  height: ${({ theme }) => theme.scale.px(38)};
  justify-self: center;
  border-radius: ${({ theme }) => theme.scale.px(40)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: center;
  padding: ${({ theme }) => theme.scale.px(5)};
  box-sizing: border-box;
  position: relative;
`;

const PillTrack = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.scale.px(5)};
  right: ${({ theme }) => theme.scale.px(5)};
  bottom: ${({ theme }) => theme.scale.px(5)};
  left: ${({ theme }) => theme.scale.px(5)};
  pointer-events: none;
`;

const ActivePill = styled.div<{ $activeFilter: TipsStatementFilter }>`
  position: absolute;
  inset: 0;
  width: 50%;
  height: 100%;
  border-radius: ${({ theme }) => theme.scale.px(40)};
  background: ${({ theme }) => theme.colors.brand.primary};
  opacity: ${({ $activeFilter }) => ($activeFilter === 'all' ? 0 : 1)};
  transform: ${({ $activeFilter }) =>
    $activeFilter === 'withdrawals' ? 'translateX(100%)' : 'translateX(0)'};
  transition: transform 160ms ease, opacity 120ms ease;
`;

const Divider = styled.span<{ $isVisible: boolean }>`
  position: absolute;
  left: 50%;
  top: ${({ theme }) => theme.scale.px(12)};
  width: ${({ theme }) => theme.scale.px(1)};
  height: ${({ theme }) => theme.scale.px(12)};
  border-radius: ${({ theme }) => theme.scale.px(1)};
  background: ${({ theme }) => theme.colors.border.emphasis};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transform: translateX(-50%);
  transition: opacity 120ms ease;
`;

const TabButton = styled(BaseButton)<{ $isActive: boolean }>`
  width: 100%;
  height: 100%;
  border-radius: ${({ theme }) => theme.scale.px(40)};
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(14)};
  color: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.text.primary : theme.colors.border.emphasis};
  position: relative;
`;

function nextFilterValue(
  activeFilter: TipsStatementFilter,
  tabFilter: Exclude<TipsStatementFilter, 'all'>,
): TipsStatementFilter {
  if (activeFilter === tabFilter) {
    return 'all';
  }

  return tabFilter;
}

export function TipsStatementFilterTabs({
  activeFilter,
  onChange,
}: TipsStatementFilterTabsProps) {
  const { t } = useTranslation();

  return (
    <Tabs>
      <PillTrack>
        <ActivePill $activeFilter={activeFilter} />
      </PillTrack>
      <Divider $isVisible={activeFilter === 'all'} />
      <TabButton
        type="button"
        $isActive={activeFilter === 'incoming'}
        onClick={() => {
          onChange(nextFilterValue(activeFilter, 'incoming'));
        }}
      >
        {t('tipsStatement.filterIncoming')}
      </TabButton>
      <TabButton
        type="button"
        $isActive={activeFilter === 'withdrawals'}
        onClick={() => {
          onChange(nextFilterValue(activeFilter, 'withdrawals'));
        }}
      >
        {t('tipsStatement.filterWithdrawals')}
      </TabButton>
    </Tabs>
  );
}
